import { UNKNOWN_BRAND_ID } from '@/modules/brands/constants';
import {
  type ProductBySlugResponse,
  type ProductBySlugExtendedResponse,
} from '@/modules/product/aliases';
import { type SimilarProduct } from '@/modules/similar/aliases';
import {
  type YMALSegmentKeys,
  type YMALSegments,
} from '@/modules/similar/types';

type SegmentFilters = Record<
  YMALSegmentKeys,
  (
    similarProduct: SimilarProduct,
    productBasic?: ProductBySlugResponse,
    productExtended?: ProductBySlugExtendedResponse
  ) => boolean
>;

const segmentFilters: SegmentFilters = {
  any: () => true,
  brand: (similarProduct, productBasic) => {
    const brandId = productBasic?.brand_id;

    if (!brandId || brandId === UNKNOWN_BRAND_ID) {
      return false;
    }

    // Exclude the current product by ID
    if (similarProduct.id === productBasic?.id) {
      return false;
    }

    return similarProduct.brand_id === brandId;
  },
  productType: (similarProduct, productBasic) => {
    if (
      !productBasic?.attributes.group ||
      !productBasic?.attributes.product_type
    ) {
      return false;
    }

    // Exclude the current product by ID
    if (similarProduct.id === productBasic?.id) {
      return false;
    }

    return (
      similarProduct.attributes?.group === productBasic?.attributes.group &&
      similarProduct.attributes?.product_type ===
        productBasic?.attributes.product_type
    );
  },
  colour: (similarProduct, _, productExtended) => {
    const colourId = productExtended?.attributes?.colour?.[0].id;

    if (colourId) {
      return colourId === similarProduct.attributes?.colour?.[0];
    }
    return false;
  },
};

export function createYMALSegments({
  product,
  productExtended,
  segmentSize,
  similarProducts: _similarProducts,
}: {
  product?: ProductBySlugResponse;
  productExtended?: ProductBySlugExtendedResponse;
  segmentSize: number;
  similarProducts: SimilarProduct[];
}) {
  const similarProducts = [..._similarProducts];

  return Object.entries(segmentFilters).reduce<YMALSegments>(
    (acc, [segmentName, filterFn]) => {
      const products = similarProducts
        .filter((similarProduct) =>
          filterFn(similarProduct, product, productExtended)
        )
        .slice(0, segmentSize);

      similarProducts.splice(0, products.length);

      acc[segmentName as YMALSegmentKeys] = products;

      return acc;
    },
    {
      any: [],
      brand: [],
      productType: [],
      colour: [],
    }
  );
}
